/* eslint-disable react-hooks/rules-of-hooks */
import React, { useState, useMemo, useRef, useCallback } from "react";
import PageQuery from "components/PageQuery";
import { DateTime } from "luxon";
import { useRouter } from "next/router";
import { mapBusinessItemData } from "lib/utils";
import styled, { css } from "styled-components";
import Pagination from "components/Pagination";
import SearchBar from "components/BusinessSearchBar";
import MapWithMarkers from "components/MapWithMarkers";
import ConditionalNavWrapper from "components/ConditionalNavWrapper";
import BusinessResultItem from "components/BusinessResultItem";
import BusinessResultsHeader from "components/BusinessResultsHeader";
import useCurrentLocation, {
  useFallbackLocation,
} from "hooks/use-current-location";
import useVendorUser from "hooks/use-vendor-user";
import useCustomerUser from "hooks/use-customer-user";
import ChangeRoleModal from "components/ChangeRoleModal";
import CustomerRedirectModal from "components/CustomerRedirectModal";
import NoSearchResults from "components/NoSearchResults";
import { NextSeo } from 'next-seo';

const GoogleMapContainer = styled.div`
  grid-area: map;
  position: relative;
  display: none;
  ${({ theme }) => theme.mq.xl`
    display: block;
    min-width: 14.9rem;
  `}
`;

const StickyMapView = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  overflow: hidden;
`;

const Title = styled.span`
  align-self: flex-start;
  font-size: 1.5rem;
  line-height: 1.75rem;
  margin-top: 0.5em;
  font-weight: 600;

  ${({ theme }) => theme.mq.sm`
  font-size: 2rem;
  margin: 0;
  line-height: 2.375rem;
  `}
`;

const ListAndMapContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-areas: "list";
  ${({ theme }) => theme.mq.xl`
    grid-template-columns: 1.3fr 1fr;
    grid-template-areas: "list map";
  `}
  transition: ${({ theme }) => theme.opacityTransition};
`;

const OuterSearchContainer = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  height: 100%;
`;

const PaginationContainer = styled.div`
  border-top: 0.0625rem solid ${({ theme }) => theme.themeColors.lightGrey};
  margin: 0 1.5rem;
  padding: 2rem 0;
  position: relative;
`;

const ResultListContainer = styled.div`
  border-right: 0.0625rem solid ${({ theme }) => theme.themeColors.lightGrey};
  grid-area: list;
  min-height: 100vh;
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  opacity: 1;
  transition: opacity 0.2s;
  ${({ isLoading }) =>
    isLoading &&
    css`
      pointer-events: none;
      opacity: 0.5;
    `}
`;

const SearchResultsContainer = styled.ul`
  /* border-bottom: 0.0625rem solid ${({ theme }) =>
    theme.themeColors.lightGrey}; */
  border-top: 0.0625rem solid ${({ theme }) => theme.themeColors.lightGrey};
  list-style-type: none;
  margin: 0;
  padding: 0;
  flex: 1 0 auto;
`;

function Search() {
  const customerUser = useCustomerUser();
  const vendorUser = useVendorUser();
  const { push, query } = useRouter();
  const queryLocation = {
    formattedAddress: query.formattedAddress,
    latitude: query.latitude,
    longitude: query.longitude,
  };
  const currentLocation = useCurrentLocation();
  const fallbackLocation = useFallbackLocation();

  // const [popped, setPopped] = useState(false);
  // useEffect(() => {
  //  function handlePop(e) {
  //    if (e.state) {
  //      setPopped(true);
  //    }
  //  }
  //  window.addEventListener("popstate", handlePop);
  //  return function cleanup() {
  //    window.removeEventListener("popstate", handlePop);
  //  };
  // }, []);

  // TODO: use another means to determine map view

  const startDateTime = useMemo(() => {
    const tomorrow = DateTime.now().plus({ days: 1 }).startOf("day");

    if (query.startDateTime) {
      const dt = DateTime.fromMillis(parseInt(query.startDateTime, 10));
      if (dt.toMillis() > tomorrow.toMillis()) {
        return dt;
      }
    }
    return tomorrow;
  }, [query.startDateTime]);

  const endDateTime = useMemo(
    () => startDateTime.plus({ days: 3 }),
    [startDateTime]
  );

  const dateArr = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 4; i += 1) {
      arr.push(startDateTime.plus({ days: i }));
    }
    return arr;
  }, [startDateTime]);

  const mobileDateArr = useMemo(() => {
    const arr = [];
    arr.push(startDateTime);
    return arr;
  }, [startDateTime]);
  const s = query.s || "all";
  const a = query.a || false;
  const initialLatitudeDisplay =
    query.latitude || currentLocation?.latitude || fallbackLocation.latitude;
  const initialLongitudeDisplay =
    query.longitude || currentLocation?.longitude || fallbackLocation.longitude;
  const initialSearchString = query.q || "";
  const currentPage = query.currentPage || 1;
  const formattedAddress =
    query.formattedAddress ||
    currentLocation?.formatted_address ||
    fallbackLocation.formatted_address;
  const ps = query.pageSize || 8;
  const radius = query.radius || 50;
  const queryObj = {
    currentPage,
    ...(ps && { pageSize: ps }),
    q: initialSearchString,
    s,
    a,
    latitude: initialLatitudeDisplay,
    longitude: initialLongitudeDisplay,
    startDateTime,
    endDateTime,
    formattedAddress,
    radius,
  };

  // eslint-disable-next-line no-unused-vars
  const updateDayColumns = (direction) => {
    let nextTime;
    if (direction === "previous") {
      nextTime = startDateTime.minus({ days: 1 });
    } else {
      nextTime = startDateTime.plus({ days: 1 });
    }

    push({
      query: {
        ...queryObj,
        startDateTime: nextTime.toMillis(),
      },
    });
  };

  const updateSelectedDay = (value) => {
    let nextTime;

    if (value) {
      const date = DateTime.fromISO(value);
      nextTime = startDateTime.set({ day: date.day, month: date.month });
    }
    push({
      query: {
        ...queryObj,
        startDateTime: nextTime.toMillis(),
      },
    });
  };

  const handleAvailabilitySort = (acceptingBooking) => {
    push({
      query: {
        ...query,
        currentPage: 1,
        a: acceptingBooking,
      },
    });
  };

  // adds 3 days in milliseconds
  const handleReadLoad = useRef(null);
  const metaDescription = `Search Results in ${formattedAddress}`;

  return (
    <ConditionalNavWrapper>
      <NextSeo
        title="GoodFriend | Search"
        description={metaDescription} 
      />
      <PageQuery
        ns="search"
        userType="customer"
        readMethod="patch"
        onReadLoad={() => handleReadLoad?.current?.()}
        query={{
          vendorBusinesses: {
            fields: [
              "businessName",
              "businessType",
              "claimedDate",
              "phone",
              "timezoneAbbreviation",
              "time_zone_id",
              "friendlyName",
              "latitude",
              "longitude",
              "logo",
              "channelTimeSlots",
              "vendorUserId",
              "firstAvailableTimeslot",
              "formattedAddress",
              "hideAddress",
              "isAcceptingBooking",
              "urlLocality",
              "urlRegion",
              "restricted",
              // "averageYotpoScore",
              // "totalYotpoReviews",
            ],
            filter: {
              ...(!query.visible && { restricted: "false" }),
              withServiceTypeNameSearchWithinDistance: queryObj,
            },
            paginate: { number: queryObj.currentPage, size: queryObj.pageSize },
            include: {
              vendorBusinessOpenHours: {
                fields: [
                  "dayOfWeek",
                  "vendorBusinessId",
                  "calcStartTime",
                  "calcEndTime",
                ],
                filter: {
                  vendorBusinessId: { fromContext: "vendorBusiness.id" },
                  onDayOfWeek: 1,
                },
              },
              serviceTypes: {
                fields: ["name", "vendorBusinessId"],
                filter: {
                  vendorBusinessId: { fromContext: "vendorBusiness.id" },
                },
              },
              channelTimeSlots: {
                readOnly: true,
                paginate: { number: 1, size: 999 },
                fields: [
                  "vendorBusinessId",
                  "pillDisplayStart",
                  "pillDisplayEnd",
                  "dayOfWeek",
                  "availabilityChannelId",
                ],
                sort: [
                  { field: "minutesSinceMidnightStart", direction: "asc" },
                ],
                filter: {
                  vendorBusinessId: { fromContext: "vendorBusiness.id" },
                  inPeriod: {
                    start_datetime: queryObj.startDateTime.toISODate(),
                    end_datetime: queryObj.endDateTime.toISODate(),
                    tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
                  },
                },
              },
              channelDateTimeSlots: {
                readOnly: true,
                paginate: { number: 1, size: 999 },
                fields: [
                  "startDatetime",
                  "endDatetime",
                  "dayOfWeek",
                  "tzDatetime",
                  "pillDisplayStart",
                  "pillDisplayEnd",
                ],
                sort: [
                  {
                    field: "startDatetime",
                    direction: "asc",
                  },
                ],
                filter: {
                  vendorBusinessId: { fromContext: "vendorBusiness.id" },
                  inPeriod: {
                    start_datetime: startDateTime.toISODate(),
                    end_datetime: endDateTime.toISODate(),
                    tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
                  },
                  uniqueByStartTime: null,
                },
              },
            },
          },
        }}
      >
        {({ data, loading }) => {
          const [activeBusinessId, setActiveBusinessId] = useState(null);
          // when a new page loads, auto-select the first item in the list
          handleReadLoad.current = useCallback(() => {
            const nextActiveId = data?.vendorBusinesses?.data?.[0]?.id;
            // we need to wait for the map to initialize before setting the
            // active ID to trigger the pin update.
            setTimeout(() => {
              setActiveBusinessId(nextActiveId);
            }, 250);
          }, [data]);
          const [showChangeRoleModal, setShowChangeRoleModal] = useState(false);
          const [showCustomerRedirectModal, setShowCustomerRedirectModal] =
            useState(false);
          const [path, setPath] = useState("");
          const [services, setServices] = useState("all");
          const showCustomerModalSetPath = (pathArg, params) => {
            const qs = new URLSearchParams(params).toString();
            setPath(`${pathArg}?${qs}`);
            setShowCustomerRedirectModal(true);
          };

          return (
            <OuterSearchContainer>
              <SearchBar setServices={setServices} />
              <ListAndMapContainer>
                <ResultListContainer isLoading={loading}>
                  <BusinessResultsHeader
                    dateArr={dateArr}
                    updateDayColumns={updateDayColumns}
                    updateSelectedDay={updateSelectedDay}
                    recordCount={data.vendorBusinesses.pagination.recordCount}
                    a={a}
                    handleAvailabilitySort={handleAvailabilitySort}
                  />
                  <SearchResultsContainer>
                    {data.vendorBusinesses.data ? (
                      data.vendorBusinesses.data.map((biz, idx) => {
                        const isClaimed = !!biz.attributes.vendorUserId;
                        const isAcceptingBooking =
                          !!biz.attributes.isAcceptingBooking;
                        return (
                          <BusinessResultItem
                            visible={query.visible}
                            isClaimed={isClaimed}
                            isAcceptingBooking={isAcceptingBooking}
                            activeBusinessId={activeBusinessId}
                            onHover={setActiveBusinessId}
                            biz={biz}
                            isCustomerUser={!!customerUser}
                            isVendorUser={!!vendorUser}
                            key={biz.id}
                            dateArr={dateArr}
                            mobileDateArr={mobileDateArr}
                            serviceTypeQueryParam={s}
                            first={idx === 0}
                            setShowChangeRoleModal={setShowChangeRoleModal}
                            showCustomerModalSetPath={showCustomerModalSetPath}
                            setShowLoginModal={setShowCustomerRedirectModal}
                            {...mapBusinessItemData(biz)}
                            updateSelectedDay={updateSelectedDay}
                          />
                        );
                      })
                    ) : (
                      <NoSearchResults />
                    )}
                  </SearchResultsContainer>
                  {data.vendorBusinesses.data ? (
                    <PaginationContainer>
                      <Pagination
                        setPageSize={(numberPageItems) => {
                          push({
                            query: {
                              ...query,
                              currentPage: 1,
                              pageSize: numberPageItems,
                            },
                          });
                        }}
                        handlePageSelection={(page) => {
                          push({
                            query: {
                              ...query,
                              currentPage: page,
                            },
                          });
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        {...data.vendorBusinesses.pagination}
                      />
                    </PaginationContainer>
                  ) : null}
                </ResultListContainer>
                <GoogleMapContainer>
                  <StickyMapView>
                    <MapWithMarkers
                      data={data}
                      currentLocation={queryLocation}
                      activeBusinessId={activeBusinessId}
                      onMarkerClick={setActiveBusinessId}
                      loading={loading}
                      services={services}
                    />
                  </StickyMapView>
                </GoogleMapContainer>
              </ListAndMapContainer>
              <CustomerRedirectModal
                asPath={path || "/"}
                businessId=""
                businessName=""
                close={() => setShowCustomerRedirectModal(false)}
                show={showCustomerRedirectModal}
                title={
                  <Title>
                    Login to book <br /> an Appointment
                  </Title>
                }
              />

              <ChangeRoleModal
                show={showChangeRoleModal}
                close={() => setShowChangeRoleModal(false)}
                userTypeToAccess="customer"
              />
            </OuterSearchContainer>
          );
        }}
      </PageQuery>
    </ConditionalNavWrapper>
  );
}

export default Search;
